import { useState } from "react";
import { Line } from "react-chartjs-2";
import type { SessionDetail } from "../../../types";
import { useTheme, chartColors } from "../../../theme";

type Mode = "per100w" | "perMin";

const MIN_WORDS = 80;
const MIN_DURATION_S = 120;

function computeRate(d: SessionDetail, mode: Mode): number | null {
  const errors = d.metrics?.total_errors ?? 0;
  if (mode === "per100w") {
    const words = d.metrics?.student_word_count ?? 0;
    if (words < MIN_WORDS) return null;
    return (errors / words) * 100;
  } else {
    const dur = d.session.duration_s;
    if (!dur || dur < MIN_DURATION_S) return null;
    return (errors / dur) * 60;
  }
}

interface Props {
  details: SessionDetail[];
}

export default function ErrorTrendChart({ details }: Props) {
  const [mode, setMode] = useState<Mode>("per100w");
  useTheme(); // re-render on theme change so the palette below is re-resolved
  const c = chartColors();

  const labels = details.map((d) => `#${d.session.id}`);
  const rates = details.map((d) => computeRate(d, mode));

  const chartData = {
    labels,
    datasets: [{
      label: mode === "per100w" ? "Err / 100 words" : "Err / session min",
      data: rates,
      borderColor: c.error,
      backgroundColor: `${c.error}14`,
      fill: true,
      tension: 0.35,
      borderWidth: 2,
      pointBackgroundColor: rates.map((r) => r === null ? "transparent" : c.error),
      pointBorderColor: rates.map((r) => r === null ? "transparent" : c.surface),
      pointRadius: rates.map((r) => r === null ? 0 : 4),
      pointHoverRadius: 6,
      spanGaps: false,
    }],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { grid: { color: c.grid }, ticks: { color: c.ticks, font: { size: 11 } } },
      y: {
        grid: { color: c.grid },
        ticks: { color: c.ticks, font: { size: 11 } },
        beginAtZero: true,
        title: {
          display: true,
          text: mode === "per100w" ? "errors / 100 words" : "errors / session min",
          color: c.ticks,
          font: { size: 10 },
        },
      },
    },
  } as const;

  const btnBase = "px-3 py-1 text-xs rounded-full transition-colors duration-150 font-medium";
  const active = `${btnBase} bg-surface2 text-ink`;
  const inactive = `${btnBase} text-text-tertiary hover:text-text-secondary`;

  return (
    <div className="flex flex-col gap-2">
      <div className="flex gap-1 justify-end">
        <button className={mode === "per100w" ? active : inactive} onClick={() => setMode("per100w")}>
          Per 100 words
        </button>
        <button className={mode === "perMin" ? active : inactive} onClick={() => setMode("perMin")}>
          Per session min
        </button>
      </div>
      <div className="h-[180px]">
        <Line data={chartData} options={chartOptions} />
      </div>
    </div>
  );
}
